import type React from "react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import emailjs from "emailjs-com"
import "../assets/css/material-kit.css"
import "../assets/css/styles.css"
import humanHumanoidImg from "../assets/img/human_humanoid_loop.gif"

const ContactUs = () => {
  const { t } = useTranslation()

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAILJS_USER_ID || "")
  }, [])

  // State to manage form inputs
  const [formData, setFormData] = useState({
    to_name: "SyncOrb-AI", // Recipient name
    from_name: "", // Sender's name
    from_email: "", // Sender's email
    message: "", // Message content
    reply_to: "", // Reply-to email (same as from_email)
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [responseMessage, setResponseMessage] = useState("")

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Update reply_to when email is changed
      ...(name === "from_email" ? { reply_to: value } : {}),
    }))
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setResponseMessage("")

    // Access environment variables
    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID
    const userID = process.env.REACT_APP_EMAILJS_USER_ID

    // Validate environment variables
    if (!serviceID || !templateID || !userID) {
      console.error("Missing EmailJS configuration:", { serviceID, templateID, userID })
      setResponseMessage(t("contact.errorMessage"))
      setIsSubmitting(false)
      return
    }

    console.log("EmailJS configuration:", { serviceID, templateID, userID })

    try {
      const result = await emailjs.send(
        serviceID,
        templateID,
        {
          to_name: formData.to_name,
          from_name: formData.from_name,
          from_email: formData.from_email,
          message: formData.message,
          reply_to: formData.reply_to,
        },
        userID,
      )

      console.log("EmailJS success:", result.text)
      setResponseMessage(t("contact.successMessage"))
      setFormData({
        to_name: "SyncOrb-AI",
        from_name: "",
        from_email: "",
        message: "",
        reply_to: "",
      })
    } catch (error) {
      console.error("EmailJS error:", error)
      console.error("Error details:", JSON.stringify(error, null, 2))
      setResponseMessage(t("contact.errorMessage"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="contact-us">
      <section>
        <div className="page-header min-vh-100">
          <div className="container">
            <div className="row">
              {/* Left Image */}
              <div className="col-lg-6 d-none d-lg-flex h-100 my-auto pe-0 position-absolute top-0 start-0 text-center justify-content-center flex-column">
                <div
                  className="position-relative h-100 m-3 px-7 border-radius-lg d-flex flex-column justify-content-center"
                  style={{
                    backgroundImage: `url(${humanHumanoidImg})`,
                    backgroundSize: "cover",
                  }}
                />
              </div>

              {/* Contact Form */}
              <div className="col-xl-5 col-lg-6 col-md-7 d-flex flex-column ms-auto me-auto ms-lg-auto me-lg-5">
                <div className="card d-flex blur justify-content-center shadow-lg my-sm-0 my-sm-6 mt-8 mb-5">
                  <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2 bg-transparent">
                    <div className="bg-gradient-info shadow-info border-radius-lg p-3">
                      <h3 className="text-white text-info mb-0">{t("contact.title")}</h3>
                    </div>
                  </div>
                  <div className="card-body">
                    <p className="pb-3">{t("contact.description")}</p>

                    <form onSubmit={handleSubmit} autoComplete="off">
                      <div className="card-body p-0 my-3">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="input-group input-group-static mb-4">
                              <label>{t("contact.form.fullNameHeader")}</label>
                              <input
                                type="text"
                                name="from_name"
                                className="form-control"
                                value={formData.from_name}
                                onChange={handleChange}
                                placeholder={t("contact.form.fullNamePlaceholder")}
                                required
                              />
                            </div>
                          </div>
                          <div className="col-md-6 ps-md-2">
                            <div className="input-group input-group-static mb-4">
                              <label>{t("contact.form.emailHeader")}</label>
                              <input
                                type="email"
                                name="from_email"
                                className="form-control"
                                value={formData.from_email}
                                onChange={handleChange}
                                placeholder={t("contact.form.emailPlaceholder")}
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="form-group mb-0 mt-md-0 mt-4">
                          <div className="input-group input-group-static mb-4">
                            <label>{t("contact.form.messageHeader")}</label>
                            <textarea
                              name="message"
                              className="form-control"
                              rows={6}
                              value={formData.message}
                              onChange={handleChange}
                              placeholder={t("contact.form.messagePlaceholder")}
                              required
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-12 text-center">
                            <button type="submit" className="btn bg-gradient-info mt-3 mb-0" disabled={isSubmitting}>
                              {isSubmitting ? t("contact.form.sendingButton") : t("contact.form.sendButton")}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>

                    {responseMessage && (
                      <div className="mt-3 text-center">
                        <p className={responseMessage.includes("error") ? "text-danger" : "text-success"}>
                          {responseMessage}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ContactUs

